// src/utils/api-helpers/RolesService.helpers.ts
// Helper functions and wrappers for RolesService
// NOTE: This file is NOT auto-generated and will NOT be overwritten by yarn generate:api
// It provides convenience methods that wrap or extend the generated RolesService

import { CancelablePromise } from '../../api/core/CancelablePromise';
import { OpenAPI } from '../../api/core/OpenAPI';
import { request as __request } from '../../api/core/request';
import type { Role, RoleFilter } from '../../types/role.types';
import { mapApiRoleToRole, type ApiRole } from '../../types/role.types';
import { RolesService } from '../../api/services/RolesService';
import type { UpdatePermissionsDto } from '../../api/models/UpdatePermissionsDto';
import type { BlockRoleDto } from '../../api/models/BlockRoleDto';

export class RolesServiceHelpers {
  /**
   * Find all roles with filters and pagination
   * @param page Page number (1-based)
   * @param limit Number of items per page
   * @param filters Filter object
   * @returns Promise with roles data
   */
  public static rolesControllerFindAllWithFilters(
    page: number,
    limit: number,
    filters: RoleFilter,
  ): CancelablePromise<any> {
    // API accepts name | createdAt | userCount (updatedAt is not supported server-side)
    let sortBy: 'name' | 'createdAt' | 'userCount' | undefined = undefined;
    if (filters.sortBy && ['name', 'createdAt', 'userCount'].includes(filters.sortBy)) {
      sortBy = filters.sortBy as 'name' | 'createdAt' | 'userCount';
    }

    // Use the generated service method with query parameters
    return RolesService.rolesControllerFindAll(
      page,
      limit,
      filters.searchTerm,
      filters.status,
      filters.startDate,
      filters.endDate,
      sortBy,
    );
  }

  /**
   * Fetch every role page (API limit is 100 per request).
   * Shared by UsersPage filters and UserWizard role pickers.
   */
  public static async fetchAllRoles(): Promise<Role[]> {
    const pageSize = 100;
    let page = 1;
    let totalPages = 1;
    const allRoles: Role[] = [];

    do {
      const response = await RolesService.rolesControllerFindAll(
        page,
        pageSize,
        undefined,
        undefined,
        undefined,
        undefined,
        'name',
      );

      if (response?.data) {
        const rolesData = (response.data as ApiRole[]).map((apiRole) => mapApiRoleToRole(apiRole));
        allRoles.push(...rolesData);
        totalPages = response.meta?.totalPages ?? 1;
      } else {
        break;
      }
      page += 1;
    } while (page <= totalPages);

    return allRoles;
  }

  /**
   * Get role permissions
   * Uses the generated RolesService method
   */
  public static rolesControllerGetPermissions(id: string): CancelablePromise<any> {
    return RolesService.rolesControllerGetPermissions(id);
  }

  /**
   * Update role permissions
   * Uses the generated RolesService method
   */
  public static rolesControllerUpdatePermissions(
    id: string,
    permissions: UpdatePermissionsDto,
  ): CancelablePromise<any> {
    return RolesService.rolesControllerUpdatePermissions(id, permissions);
  }

  /**
   * Block a role
   * Wrapper around rolesControllerBlockRole
   */
  public static rolesControllerBlock(
    id: string,
    requestBody?: BlockRoleDto,
  ): CancelablePromise<any> {
    return RolesService.rolesControllerBlockRole(id, requestBody || {});
  }

  /**
   * Unblock a role
   * Wrapper around rolesControllerUnblockRole
   */
  public static rolesControllerUnblock(id: string): CancelablePromise<any> {
    return RolesService.rolesControllerUnblockRole(id);
  }

  /**
   * Get users with a specific role
   * GET /roles/:id/users
   */
  public static rolesControllerGetUsers(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: `/roles/${id}/users`,
    });
  }

  /**
   * Copy a role
   * POST /roles/:id/copy
   */
  public static rolesControllerCopy(id: string, body?: { name?: string }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: `/roles/${id}/copy`,
      body: body || {},
      mediaType: 'application/json',
    });
  }
}
